// 1:1 port of customer-apps/offering/models.ts — field names and types are the API contract
// and must not be renamed (APP_MIGRATE.md §11).
import type { DataResult } from '@shared/http/client';
import type { TranslatableOrString } from '@shared/composables/useBatchedTranslation';

export interface Fraction {
    numerator: number
    denominator: number
}

export function fractionToNumber(fraction: Fraction): number {
    return fraction.numerator / fraction.denominator;
}

export type BookingMode = 'offering' | 'appointment';
export type DeletedByType = 'club' | 'member';
export type OfferingView = 'list' | 'timetable';

/**
 * customer-apps/offering/components/offering-view-options — `ViewOptions` lives with the types
 *  because `<script setup>` cannot export one.
 */
export interface ViewOptions {
    selectedView: OfferingView
    fullscreen: boolean
}

export type OfferingType = 'generic' | 'course' | 'exam' | 'training';

export const OfferingPricingType = {
    free: 'free',
    all: 'all',
    activeMembersPrice: 'active_members_price',
    passiveMembersPrice: 'passive_members_price',
    guestMembersPrice: 'guest_members_price',
    teamPrice: 'team_price',
    departmentPrice: 'department_price',
} as const;
export type OfferingPricingTypeValue = (typeof OfferingPricingType)[keyof typeof OfferingPricingType];

/** 1:1 port of club-employee-app/src/app/offering/models.ts — the employee attendance tracking. */
export type AttendeeStatus = 'present' | 'absent' | 'none';

export type TrainingAppointmentResponse = 'accepted' | 'unsure' | 'rejected';

export const Interval = {
    daily: 'daily',
    weekly: 'weekly',
    biWeekly: 'biweekly',
    mondayToFriday: 'mondayToFriday',
    mondayToSaturday: 'mondayToSaturday',
    monthly: 'monthly',
    quarterly: 'quarterly',
    halfYearly: 'half-yearly',
    yearly: 'yearly',
} as const;
export type IntervalValue = (typeof Interval)[keyof typeof Interval];

export const OfferingExecutionType = {
    presence: 'presence',
    online: 'online',
    hybrid: 'hybrid',
} as const;
export type OfferingExecutionTypeValue = (typeof OfferingExecutionType)[keyof typeof OfferingExecutionType];

export type OfferingTargetGroup = 'member' | 'guest' | 'all';

export const BookingState = {
    booked: 'booked',
    canceled: 'canceled',
    waitListWaiting: 'waitListWaiting',
    waitListCurrentlyBookable: 'waitListCurrentlyBookable',
    open: 'open',
} as const;
export type BookingStateValue = (typeof BookingState)[keyof typeof BookingState];

export interface OfferingCategory {
    id: string
    name: string
    translatableName: TranslatableOrString
    backgroundColor: string
}

export interface Sport {
    id: string
    name: TranslatableOrString
}

export interface Instructor {
    id: number
    fullName: string
}

export interface OfferingLocation {
    id: string
    name: string
    street: string
    addressCo?: string
    city: string
    postalCode: string
    areas: LocationArea[]
}

export interface LocationWithField extends OfferingLocation {
    fieldId: string
    fieldName: string
    areaId: string
    areaName: string
}

export interface LocationArea {
    id: string
    locationId: string
    name: string
    fields: LocationAreaField[]
}

export interface LocationAreaField {
    id: string
    locationId: string
    areaId: string
    name: string
}

export interface OfferingAppointmentOverride {
    title?: string
    note?: string
    isCanceled: boolean
    instructors: Instructor[]
    locations: LocationWithField[]
}

export interface OfferingAppointment {
    isEditable?: boolean
    canCreateBookings?: boolean
    attendeesEnabled?: boolean
    appointmentSeries: AppointmentSeries
    bookingMode: BookingMode
    start: string
    end: string
    bookingStart?: string
    bookingEnd?: string
    totalAvailablePlaces?: number
    remainingAvailablePlaces?: number
    hasAvailablePlaces: boolean
    prices: OfferingPricing[]
    memberPrices: OfferingPricing[]
    offering: Offering
    appointmentOverride?: OfferingAppointmentOverride
    waitListActive: boolean
    waitListEntries: WaitListEntry[]
    icsDownloadApiUrl: string
    icsPreviewUrl?: string
}

export interface WaitListEntry {
    offeringId?: string
    appointmentSeriesId?: string
    appointmentStart?: string
    appointmentTitle?: string
    offeringType?: OfferingType
    id: string
    member: OfferingMember
    reservedByMember: OfferingMember
    reservedUntil: string | null
}

export interface AppointmentSeries {
    id: string
    interval?: string | IntervalValue | null
    intervalStart?: string
    intervalEnd?: string
}

export interface BookableOfferingAppointment extends OfferingAppointment {
    isCancellable: boolean
    bookings: OfferingBooking[]
    conditionsOfParticipationUrl: string
}

export interface TrainingOfferingAppointment extends OfferingAppointment {
    offering: TrainingOffering
    replies: TrainingAppointmentReply[]
}

export interface OfferingBooking {
    member: OfferingMember
    invoiceMember: OfferingMember
    bookedByMember: OfferingMember
    dog?: OfferingDog
    clubExams?: ClubExam[]
    price: string
    priceAsFraction: Fraction
    bookingId?: string
    createdAt?: string
    deletedAt?: string
    deletedBy?: DeletedByType
    onlineUrl?: string
    comment?: string
    bookedSalesListOfferingItems?: BookedSalesListOfferingItem[]
    cancellationNote?: string
}

export interface TrainingAppointmentReply {
    createdAt: string
    member: OfferingMember
    responseFromMember: OfferingMember
    response: TrainingAppointmentResponse | null
    comment?: string
}

export interface OfferingPricing {
    money: string
    moneyAsFraction: Fraction
    currency: string
    type: string | OfferingPricingTypeValue
    memberId?: number
    memberFullName?: string
    dogs?: OfferingDog[]
    // rendered by OfferingPrice for the department/team pricing types
    department?: string
    team?: string
}

export interface OfferingMember {
    id: number
    fullName: string
}

export interface OfferingDog {
    id: number
    name: string
}

export interface PricePerTime {
    data: {
        type: string
        currency: string
        netOrGross: 'gross' | 'net'
        net: DataResult<Fraction>
        gross: DataResult<Fraction>
        vat: DataResult<Fraction>
    }
}

export interface SalesListOfferingItem {
    id: string
    clubFeeId: string
    clubFeeName: string
    pricePerTime: PricePerTime
}

export interface BookedSalesListOfferingItem extends Fraction {
    memberClubFeeId: string
    clubFeeName: string
    pricePerTime: PricePerTime
}

export interface SelectedSalesListOfferingItem extends Fraction {
    id: string
}

export interface Offering {
    id: string
    customId: string
    isFavorite: boolean
    type: OfferingType
    category: OfferingCategory
    executionType: OfferingExecutionTypeValue
    instructors: Instructor[]
    title: string
    description?: string
    minimumAge?: number
    maximumAge?: number
    minParticipants?: number
    maxParticipants?: number
    registrationRequested: boolean
    showAvailablePlaces: boolean
    bookableForMen: boolean
    bookableForWomen: boolean
    bookableForActiveMembers: boolean
    bookableForPassiveMembers: boolean
    bookableForGuestMembers: boolean
    bookableDepartmentNames: string[]
    bookableTeamNames: string[]
    bookingStart?: string
    bookingEnd?: string
    bookingStartPerAppointmentStartDate?: string
    locations: LocationWithField[]
    sport: TranslatableOrString
    sportColor: string
    requirements?: string
    showCommentField: boolean
    commentFieldCaption?: TranslatableOrString
    hasImage: boolean
    imageApiUrl: string
    salesListOfferingItems: DataResult<SalesListOfferingItem>[]
}

export type ExamineeType = 'member' | 'dog';

export interface ExamOffering extends Offering {
    type: 'exam'
    examineeType: ExamineeType
    clubExams: ClubExam[]
    selectableExams: boolean
}

export interface ClubExam {
    id: string
    title: string
    examineeType?: ExamineeType
}

export interface TrainingOffering extends Offering {
    type: 'training'
}

export type GenderFilterValue = 'male' | 'female';

export type StateFilterValue = null | 'onlyBooked' | 'onlyFavorites';

export interface OfferingFilterDateRange {
    start: string | null
    end: string | null
}

export interface OfferingFilterTime {
    hours: number | null
    minutes: number | null
}

export interface OfferingFilterTimeRange {
    start: OfferingFilterTime | null
    end: OfferingFilterTime | null
}

export interface OfferingFiltersQueryParams {
    state?: StateFilterValue
    query?: string | null
    categoryIds?: string[] | string
    sportIds?: string[] | string
    departmentIds?: number[] | number | string[] | string
    teamIds?: number[] | number | string[] | string
    instructorIds?: string[] | string
    locationIds?: string[] | string
    areaIds?: string[] | string
    fieldIds?: string[] | string
    executionType?: 'presence' | 'online'
    weekdays?: string[] | string
    startDateRange?: string // JSON encoded OfferingFilterDateRange
    startTimeRange?: string // JSON encoded OfferingFilterTimeRange
    free?: 'false' | 'true'
    gender?: GenderFilterValue
    age?: string
    targetGroup?: OfferingTargetGroup
    // might contain additional parameters that should be ignored by filters
    [key: string]: unknown
}

export interface OfferingFilters {
    query?: string | null
    state?: StateFilterValue
    teamIds?: string[]
    categoryIds?: string[]
    sportIds?: string[]
    departmentIds?: number[]
    instructorIds?: number[]
    locations?: OfferingLocation[]
    areas?: LocationArea[]
    fields?: LocationAreaField[]
    executionType?: 'presence' | 'online' | null
    weekdays?: string[]
    startDateRange?: OfferingFilterDateRange | null
    startTimeRange?: OfferingFilterTimeRange | null
    free?: boolean | null
    onlyBooked?: boolean
    isFavorite?: boolean
    includeCanceled?: boolean
    includePast?: boolean
    includeWaitListed?: boolean
    gender?: GenderFilterValue | null
    age?: number | null
    targetGroup?: OfferingTargetGroup
}

export type OfferingFiltersApiQueryParams = Record<string, string | number | (string | number)[]>;

export interface OfferingBookingGuestDto {
    firstName: string
    lastName: string
    gender: string | null
    email?: string | null
    dateOfBirth: string
    emergencyContact: string
}

export type CategoryNameFilter = 'course' | 'exam' | 'training';
